import type { PalaceId, StarId } from './domain.js';
import {
  RELATIONSHIP_TIMING_SCOPES,
  type RelationshipTimingContextBundle,
  type RelationshipTimingScope,
} from './relationshipTimingContext.js';

export type RelationshipTimingConvergenceKind =
  | 'person_focus_cycle_palace'
  | 'shared_mutagen'
  | 'shared_focus_moving_star'
  | 'shared_focus_cycle_palace';

export interface RelationshipTimingConvergenceFact {
  id: string;
  kind: RelationshipTimingConvergenceKind;
  role: 'A' | 'B' | 'BOTH';
  key: string;
  scopes: readonly RelationshipTimingScope[];
  layerCount: number;
}

export interface RelationshipTimingConvergenceSummary {
  model: 'RELATIONSHIP-TIMING-CONVERGENCE-0.1.0';
  repeatedFactCount: number;
  maxLayerCount: number;
  facts: readonly RelationshipTimingConvergenceFact[];
  disclosure: string;
}

const SCOPE_ORDER = new Map(RELATIONSHIP_TIMING_SCOPES.map((scope, index) => [scope, index] as const));

function sortedScopes(scopes: Iterable<RelationshipTimingScope>): readonly RelationshipTimingScope[] {
  return Object.freeze([...new Set(scopes)].sort((a, b) => (SCOPE_ORDER.get(a) ?? 99) - (SCOPE_ORDER.get(b) ?? 99)));
}

function addScope<K>(map: Map<K, RelationshipTimingScope[]>, key: K, scope: RelationshipTimingScope): void {
  const list = map.get(key);
  if (list) list.push(scope);
  else map.set(key, [scope]);
}

function fact(
  kind: RelationshipTimingConvergenceKind,
  role: 'A' | 'B' | 'BOTH',
  key: string,
  scopes: Iterable<RelationshipTimingScope>,
): RelationshipTimingConvergenceFact | null {
  const normalizedScopes = sortedScopes(scopes);
  if (normalizedScopes.length < 2) return null;
  return Object.freeze({
    id: `relationship_timing.convergence.${kind}.${role.toLowerCase()}.${key}`,
    kind,
    role,
    key,
    scopes: normalizedScopes,
    layerCount: normalizedScopes.length,
  });
}

export function buildRelationshipTimingConvergence(
  context: RelationshipTimingContextBundle,
): RelationshipTimingConvergenceSummary {
  const facts: RelationshipTimingConvergenceFact[] = [];

  for (const person of [context.personA, context.personB] as const) {
    const byPalace = new Map<PalaceId, RelationshipTimingScope[]>();
    for (const item of person.scopes) addScope(byPalace, item.focusCyclePalaceId, item.scope);
    for (const [palaceId, scopes] of byPalace) {
      const item = fact('person_focus_cycle_palace', person.role, palaceId, scopes);
      if (item) facts.push(item);
    }
  }

  const sharedMutagens = new Map<string, RelationshipTimingScope[]>();
  const sharedMovingStars = new Map<StarId, RelationshipTimingScope[]>();
  const sameFocusScopes: RelationshipTimingScope[] = [];
  for (const shared of context.sharedScopes) {
    if (shared.sameFocusCyclePalace) sameFocusScopes.push(shared.scope);
    for (const key of shared.sharedMutagenKeys) addScope(sharedMutagens, key, shared.scope);
    for (const starId of shared.sharedFocusMovingStarIds) addScope(sharedMovingStars, starId, shared.scope);
  }

  for (const [key, scopes] of sharedMutagens) {
    const item = fact('shared_mutagen', 'BOTH', key, scopes);
    if (item) facts.push(item);
  }
  for (const [starId, scopes] of sharedMovingStars) {
    const item = fact('shared_focus_moving_star', 'BOTH', starId, scopes);
    if (item) facts.push(item);
  }
  const sameFocus = fact('shared_focus_cycle_palace', 'BOTH', 'same_focus_cycle_palace', sameFocusScopes);
  if (sameFocus) facts.push(sameFocus);

  facts.sort((a, b) => a.id.localeCompare(b.id));
  return Object.freeze({
    model: 'RELATIONSHIP-TIMING-CONVERGENCE-0.1.0',
    repeatedFactCount: facts.length,
    maxLayerCount: facts.reduce((max, item) => Math.max(max, item.layerCount), 0),
    facts: Object.freeze(facts),
    disclosure: '跨層重複只表示同一客觀結構在兩個以上運限層級重複出現；layerCount 是出現層數，不是吉凶分數、相合率、信心值或事件機率。',
  });
}

const SCOPE_LABELS: Readonly<Record<RelationshipTimingScope, string>> = Object.freeze({
  decadal: '大限',
  minor: '小限',
  yearly: '流年',
  monthly: '流月',
  daily: '流日',
  hourly: '流時',
});

const KIND_LABELS: Readonly<Record<RelationshipTimingConvergenceKind, string>> = Object.freeze({
  person_focus_cycle_palace: '焦點宮映射跨層重複',
  shared_mutagen: '共同四化跨層重複',
  shared_focus_moving_star: '共同流耀跨層重複',
  shared_focus_cycle_palace: '雙方同焦點宮映射跨層重複',
});

export interface RelationshipTimingConvergenceFactViewModel {
  id: string;
  kind: RelationshipTimingConvergenceKind;
  kindLabel: string;
  role: RelationshipTimingConvergenceFact['role'];
  key: string;
  layerCount: number;
  scopeLabels: readonly string[];
}

export interface RelationshipTimingConvergenceViewModel {
  repeatedFactCount: number;
  maxLayerCount: number;
  facts: readonly RelationshipTimingConvergenceFactViewModel[];
  emptyState?: string;
  disclosure: string;
}

export function relationshipTimingConvergenceViewModel(
  context: RelationshipTimingContextBundle,
): RelationshipTimingConvergenceViewModel {
  const summary = buildRelationshipTimingConvergence(context);
  return Object.freeze({
    repeatedFactCount: summary.repeatedFactCount,
    maxLayerCount: summary.maxLayerCount,
    facts: Object.freeze(summary.facts.map((item) => Object.freeze({
      id: item.id,
      kind: item.kind,
      kindLabel: KIND_LABELS[item.kind],
      role: item.role,
      key: item.key,
      layerCount: item.layerCount,
      scopeLabels: Object.freeze(item.scopes.map((scope) => SCOPE_LABELS[scope])),
    }))),
    ...(summary.facts.length === 0 ? { emptyState: '目前沒有同一客觀結構跨兩個以上運限層級重複出現。' } : {}),
    disclosure: summary.disclosure,
  });
}
